import React, {
  type Dispatch,
  type FC,
  SetStateAction,
  useMemo,
  useState,
  RefObject,
} from "react";
import clsx from "clsx";
import { useLocale } from "hooks";
import translation, { PLAYING_MODE } from "./translation";
import SidebarCard from "./SidebarCard";
import style from "./Sidebar.module.scss";
import SharingModal from "./SharingModal";

interface IProps {
  activeItem: Object | null;
  onSidebarItemClick: Function;
  videos: Array<any>;
  playingMode: PLAYING_MODE;
  setPlayingMode: Dispatch<SetStateAction<PLAYING_MODE>>;
  getLinkAndUpdateShareLinkList: (
    item: any
  ) => Promise<{ raw: string; hashed: string }>;
  mainSideRef: RefObject<HTMLDivElement>;
}

const Sidebar: FC<IProps> = ({
  videos,
  onSidebarItemClick,
  activeItem,
  playingMode,
  setPlayingMode,
  getLinkAndUpdateShareLinkList,
  mainSideRef,
}) => {
  const { isRTL, locale } = useLocale();
  const activeIndex = useMemo<number>(
    () => videos?.findIndex?.((item) => item?.id === activeItem?.id),
    [activeItem, videos]
  );
  const [activeMoreOption, setActiveMoreOption] = useState<Object | null>(null);
  const [activeModalItem, setActiveModalItem] = useState<Object | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  return (
    <div
      className={clsx(
        "tw-py-4 tw-rounded-lg tw-border tw-border-[#E3E7EB] tw-border-solid",
        style.sidebar
      )}
    >
      <div className="tw-px-4 tw-flex tw-justify-between tw-items-center">
        <div>
          <h2 className="tw-text-sm md:tw-text-base">
            {translation?.[locale]?.VIDEO_LIST}
          </h2>
          <p className="tw-text-[12px] tw-text-[#344456]/40">
            {translation?.[locale]?.TOTAL_VIDEOS}: {activeIndex + 1}{" "}
            {translation[locale]?.OF} {videos?.length}
          </p>
        </div>
        <div>
          <button
            title={translation[locale]?.PLAY_IN_ORDER}
            onClick={() => setPlayingMode(PLAYING_MODE.ORDERED)}
            name="in-order"
            className={clsx(
              "tw-bg-zero tw-border-0 tw-cursor-pointer hover:tw-opacity-60",
              {
                "tw-ml-4": isRTL,
                "tw-mr-4": !isRTL,
              }
            )}
          >
            <svg
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.5 15.4834L4.62499 15.4918C5.38332 15.4918 6.09166 15.1167 6.50833 14.4917L11.8333 6.50844C12.25 5.88344 12.9583 5.5001 13.7167 5.50843L17.5083 5.52512"
                stroke={clsx({
                  "#292D32": playingMode !== PLAYING_MODE.ORDERED,
                  blue: playingMode === PLAYING_MODE.ORDERED,
                })}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15.832 17.1501L17.4987 15.4834"
                stroke={clsx({
                  "#292D32": playingMode !== PLAYING_MODE.ORDERED,
                  blue: playingMode === PLAYING_MODE.ORDERED,
                })}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.40835 7.68323L6.50833 6.43323C6.08333 5.84156 5.39999 5.49157 4.67499 5.4999L2.5 5.50824"
                stroke={clsx({
                  "#292D32": playingMode !== PLAYING_MODE.ORDERED,
                  blue: playingMode === PLAYING_MODE.ORDERED,
                })}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.8086 13.3167L11.8253 14.625C12.2503 15.175 12.9169 15.5 13.6169 15.5L17.5086 15.4833"
                stroke={clsx({
                  "#292D32": playingMode !== PLAYING_MODE.ORDERED,
                  blue: playingMode === PLAYING_MODE.ORDERED,
                })}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M17.4987 5.51676L15.832 3.8501"
                stroke={clsx({
                  "#292D32": playingMode !== PLAYING_MODE.ORDERED,
                  blue: playingMode === PLAYING_MODE.ORDERED,
                })}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <button
            title={translation[locale]?.PALY_IN_REPEAT}
            onClick={() => setPlayingMode(PLAYING_MODE.REPEATED)}
            data-playing-mode={PLAYING_MODE.REPEATED}
            name="repeat"
            className={clsx(
              "tw-bg-zero tw-border-0 tw-cursor-pointer hover:tw-opacity-60"
            )}
          >
            <svg
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.6685 3L13.7018 4.95002L7.07682 4.93334C4.10182 4.93334 1.66016 7.37502 1.66016 10.3667C1.66016 11.8584 2.26848 13.2167 3.25181 14.2"
                stroke={clsx({
                  "#292D32": playingMode !== PLAYING_MODE.REPEATED,
                  blue: playingMode === PLAYING_MODE.REPEATED,
                })}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.33412 18.0001L6.30078 16.05L12.9258 16.0667C15.9008 16.0667 18.3424 13.625 18.3424 10.6334C18.3424 9.1417 17.7341 7.78338 16.7508 6.80005"
                stroke={clsx({
                  "#292D32": playingMode !== PLAYING_MODE.REPEATED,
                  blue: playingMode === PLAYING_MODE.REPEATED,
                })}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
      <div
        style={{
          maxHeight: mainSideRef.current?.clientHeight || "unset",
        }}
        className={clsx(
          "tw-border tw-border-[#E3E7EB] tw-rounded-lg tw-mt-2 tw-overflow-y-auto tw-overflow-x-hidden",
          "sidebar-scrollable"
        )}
      >
        {videos?.map((item, i) => (
          <SidebarCard
            item={item}
            key={item?.id}
            index={i}
            onClick={onSidebarItemClick}
            isActive={activeItem?.id === item?.id}
            activeMoreOption={activeMoreOption}
            setActiveMoreOption={setActiveMoreOption}
            setActiveModalItem={setActiveModalItem}
            setIsModalOpen={setIsOpen}
          />
        ))}
      </div>
      <SharingModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        item={activeModalItem}
        getLinkAndUpdateShareLinkList={getLinkAndUpdateShareLinkList}
      />
    </div>
  );
};

export default Sidebar;
